import { unlockTimesheet, approveTimesheet, approveManagerApproval, approveSupervisorApproval, approveApprovalGroupApproval, updateSickClaimAmount, openPrintPreview, refreshTimesheet, openBatchPrintPreview, openBatchPrintPreviewAll, updateTimesheetSupervisor } from "../api/timesheet"; 
import {Day} from "../interfaces/EmployeeInterface";
import { PayPeriod } from "../interfaces/PayPeriodInterface";
import { TimesheetType } from "../interfaces/TimesheetInterface";
import { JobStatus } from "../interfaces/V2Interface";

// Function to unlock a timesheet based on the timesheetID and adminEmployeeID
const unlock = async (timesheetID: number, adminEmployeeID: number): Promise<any> => {

    try {
        // Make the API request to unlock the timesheet
        const apiResults = await unlockTimesheet(timesheetID, adminEmployeeID);

        // Log the API results for debugging purposes
        console.log(apiResults);

        // Check if the API results are not returned or empty, throw an error
        if (!apiResults) { 
            throw new Error("Unlock Failed.");
        }

        // Store the results from the API response
        const authData = apiResults;

        // Log the authData for debugging purposes
        console.log(authData);

        // Return the authData to be used elsewhere
        return authData;

    } catch (err) {
        // Log any errors encountered during the unlock operation
        console.error("Error in unlockTimesheet", err);

        // Throw the error for handling in calling code
        throw err;
    }

};

// Function to approve an approval group based on the timesheetID, adminEmployeeID, and requiredApprovalGroupsID
const approveApprovalGroup = async (timesheetID: number, adminEmployeeID: number, requiredApprovalGroupsID: number): Promise<any> => {

    try {
        // Make the API request to approve the approval group
        const apiResults = await approveApprovalGroupApproval(timesheetID, adminEmployeeID, requiredApprovalGroupsID);

        // Log the API results for debugging purposes
        console.log(apiResults);

        // Check if the API results are not returned or empty, throw an error
        if (!apiResults) { 
            throw new Error("Approval Group Approve Failed.");
        }

        // Store the results from the API response
        const authData = apiResults;

        // Log the authData for debugging purposes
        console.log(authData);

        // Return the authData to be used elsewhere
        return authData;

    } catch (err) {
        // Log any errors encountered during the approval group approval operation
        console.error("Error in approveApprovalGroupApproval", err);

        // Throw the error for handling in calling code
        throw err;
    }

};
// Function to approve a timesheet based on the timesheetID, adminEmployeeID, and type
const approve = async (timesheetID: number, adminEmployeeID: number, type: TimesheetType): Promise<any> => {

    try {
        // Make the API request to approve the timesheet
        const apiResults = await approveTimesheet(timesheetID, adminEmployeeID, type);

        // Log the API results for debugging purposes
        console.log(apiResults);

        // Check if the API results are not returned or empty, throw an error
        if (!apiResults) { 
            throw new Error("Approve Failed.");
        }

        // Store the results from the API response
        const authData = apiResults;

        // Log the authData for debugging purposes
        console.log(authData);

        // Return the authData to be used elsewhere
        return authData;

    } catch (err) {
        // Log any errors encountered during the approval operation
        console.error("Error in approveTimesheet", err);

        // Throw the error for handling in calling code
        throw err;
    }

};

// Function to approve a manager approval based on the timesheetID, adminEmployeeID, and requiredManagerApprovalID
const approveManager = async (timesheetID: number, adminEmployeeID: number, requiredManagerApprovalID: number): Promise<any> => {

    try {
        // Make the API request to approve the manager approval
        const apiResults = await approveManagerApproval(timesheetID, adminEmployeeID, requiredManagerApprovalID);

        // Log the API results for debugging purposes
        console.log(apiResults);

        // Check if the API results are not returned or empty, throw an error
        if (!apiResults) { 
            throw new Error("Manager Approve Failed.");
        }

        // Store the results from the API response
        const authData = apiResults;

        // Log the authData for debugging purposes
        console.log(authData);

        // Return the authData to be used elsewhere
        return authData;

    } catch (err) {
        // Log any errors encountered during the manager approval operation
        console.error("Error in approveManagerApproval", err);

        // Throw the error for handling in calling code
        throw err;
    }

};

// Function to approve a supervisor approval based on the timesheetID, adminEmployeeID, and optional requiredSuperApprovalID
const approveSupervisor = async (timesheetID: number, adminEmployeeID: number, requiredSuperApprovalID?: number): Promise<any> => {

    try {
        // Make the API request to approve the supervisor approval
        const apiResults = await approveSupervisorApproval(timesheetID, adminEmployeeID, requiredSuperApprovalID);

        // Log the API results for debugging purposes
        console.log(apiResults);

        // Check if the API results are not returned or empty, throw an error
        if (!apiResults) { 
            throw new Error("Supervisor Approve Failed.");
        }

        // Store the results from the API response
        const authData = apiResults;

        // Log the authData for debugging purposes
        console.log(authData);

        // Return the authData to be used elsewhere
        return authData;

    } catch (err) {
        // Log any errors encountered during the supervisor approval operation
        console.error("Error in approveSupervisorApproval", err);

        // Throw the error for handling in calling code
        throw err;
    }

};

// Function to update a sick claim amount based on employeeSickClaimID and amountClaimed
const changeSickClaimAmount = async (employeeSickClaimIDs: number[], hourlyRate: number): Promise<any> => {
    try {
        const apiResults = await updateSickClaimAmount(employeeSickClaimIDs, hourlyRate);

        console.log(apiResults);

        if (!apiResults || !apiResults.success) {
            throw new Error("Update Sick Claim Amount Failed.");
        }

        return apiResults;

    } catch (err) {
        console.error("Error in updateSickClaimAmount", err);
        throw err;
    }
};

const createPrintPreview = async (employeeID: number, payPeriodID: number, altID: string, payPeriod: PayPeriod): Promise<any> => {
    try {
        const apiResults = await openPrintPreview(employeeID, payPeriodID, altID, payPeriod);

        console.log(apiResults);

        return apiResults;

    } catch (err) {
        console.error("Error in openPrintPreview", err);
        throw err;
    }
};

const createBatchPrintPreview = async (supervisorID: number, payPeriodID: number, altSupervisorID: string, payPeriodObj: any): Promise<any> => {
    try {
        const apiResults = await openBatchPrintPreview(supervisorID, payPeriodID, altSupervisorID, payPeriodObj);
        console.log(apiResults);
        return apiResults;
    } catch (err) {
        console.error("Error in openBatchPrintPreview", err);
        throw err;
    }
};

const createBatchPrintPreviewAll = async (
    payPeriodID: number,
    payPeriodObj: any,
    signal?: AbortSignal,
    onProgress?: (jobStatus: JobStatus) => void,
    locationID?: number,
    locationLabel?: string
): Promise<any> => {
    try {
        const apiResults = await openBatchPrintPreviewAll(payPeriodID, payPeriodObj, signal, onProgress, locationID, locationLabel);
        console.log(apiResults);
        return apiResults;
    } catch (err) {
        console.error("Error in openBatchPrintPreviewAll", err);
        throw err;
    }
};

const getEmployeeTimesheet = async (employeeID: number, payPeriodID: number): Promise<Day[]> => {
    try {
        const apiResults = await refreshTimesheet(employeeID, payPeriodID);

        console.log(apiResults);

        return apiResults;

    } catch (err) {
        console.error("Error in openPrintPreview", err);
        throw err;
    }
}

// Function to update supervisor for a timesheet
const updateSupervisor = async (timesheetID: number, supervisorID: number, adminEmployeeID: number): Promise<any> => {

    try {
        // Make the API request to update the supervisor
        const apiResults = await updateTimesheetSupervisor(timesheetID, supervisorID, adminEmployeeID);

        // Log the API results for debugging purposes
        console.log(apiResults);

        // Check if the API results are not returned or empty, throw an error
        if (!apiResults) { 
            throw new Error("Update Supervisor Failed.");
        }

        // Store the results from the API response
        const authData = apiResults;

        // Log the authData for debugging purposes
        console.log(authData);

        // Return the authData to be used elsewhere
        return authData;

    } catch (err) {
        // Log any errors encountered during the supervisor update operation
        console.error("Error in updateTimesheetSupervisor", err);

        // Throw the error for handling in calling code
        throw err;
    }

};

// Export the unlock and approve functions to be used in other parts of the application
export { unlock, approve, approveManager, approveSupervisor, approveApprovalGroup, changeSickClaimAmount, createPrintPreview, createBatchPrintPreview, createBatchPrintPreviewAll, getEmployeeTimesheet, updateSupervisor };
